import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert, TouchableOpacity, Platform, Image, Linking} from 'react-native';
import {
  Text, Card, Button, Chip, Appbar, Divider,
  ActivityIndicator, Dialog, Portal, RadioButton,
  IconButton, Menu, TextInput,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import RNPrint from 'react-native-print';
import DateTimePicker from '@react-native-community/datetimepicker';
import {useOrderStore} from '../../store/useOrderStore';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {colors, spacing} from '../../theme';
import ReceivePaymentModal from './ReceivePaymentModal';
import CouponSelectModal from './CouponSelectModal';
import OrderLineImagesModal from './OrderLineImagesModal';
import WorkAssignmentModal from './WorkAssignmentModal';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';

const ALL_STATUSES = ['NEW', 'IN_PROCESS', 'COMPLETED', 'QUALITY_CHECK', 'READY_FOR_TRIAL', 'DELIVERED', 'CANCELLED'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateDdMmm = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  const day = d.getDate();
  const suffix = (day >= 11 && day <= 13) ? 'th'
    : day % 10 === 1 ? 'st'
    : day % 10 === 2 ? 'nd'
    : day % 10 === 3 ? 'rd' : 'th';
  return `${day}${suffix} ${MONTHS[d.getMonth()]}`;
};

const OrderViewScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {orderId} = route.params;

  const {currentOrder, isLoading, fetchOrderDetail, deleteLine,
    assignWorkerToInstance, clearCurrentOrder, applyDiscount, removeDiscount,
    updateOrderStatus, updateOrderDate, updateInstanceStatus} = useOrderStore();
  const [refreshing, setRefreshing] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  // Status edit modal
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Instance status edit modal
  const [instanceStatusModalVisible, setInstanceStatusModalVisible] = useState(false);
  const [instanceStatusTarget, setInstanceStatusTarget] = useState(null);
  const [selectedInstanceStatus, setSelectedInstanceStatus] = useState(null);

  // Date picker
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Discount state
  const [discountType, setDiscountType] = useState(null); // null | 'FLAT' | 'PERCENT' | 'COUPON'
  const [discountValue, setDiscountValue] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [discountMenuVisible, setDiscountMenuVisible] = useState(false);
  const [hasUnsavedDiscount, setHasUnsavedDiscount] = useState(false);
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Image modal
  const [imageModalLine, setImageModalLine] = useState(null);

  // Work assignment state
  const [assignModalTarget, setAssignModalTarget] = useState(null); // {instanceId, catalogItemId, serviceCatalogItemId}
  const [instanceAssignments, setInstanceAssignments] = useState({}); // {instanceId: [assignments]}

  useEffect(() => {
    fetchOrderDetail(orderId);
    return () => clearCurrentOrder();
  }, [orderId, fetchOrderDetail, clearCurrentOrder]);

  // Fetch work assignments for all instances
  const fetchAllAssignments = useCallback(() => {
    if (!currentOrder?.lines) return;
    const instances = currentOrder.lines
      .filter(l => l.instances?.length > 0)
      .map(l => l.instances[0]);
    if (instances.length === 0) return;
    Promise.all(
      instances.map(inst =>
        workAssignmentApi.getByInstance(inst.id)
          .then(res => ({id: inst.id, data: res.data?.data || []}))
          .catch(() => ({id: inst.id, data: []}))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.data; });
      setInstanceAssignments(map);
    });
  }, [currentOrder?.lines]);

  useEffect(() => {
    fetchAllAssignments();
  }, [fetchAllAssignments]);

  // Initialize discount state from existing order
  useEffect(() => {
    if (currentOrder?.discount) {
      const d = currentOrder.discount;
      if (d.adjustmentType === 'COUPON') {
        setDiscountType('COUPON');
        setSelectedCoupon({id: d.couponId, code: d.couponCode, value: d.value, type: d.method === 'PERCENT' ? 'PERCENTAGE' : 'FIXED_AMOUNT'});
        setDiscountValue(String(d.value));
      } else if (d.method === 'PERCENT') {
        setDiscountType('PERCENT');
        setDiscountValue(String(d.value));
        setSelectedCoupon(null);
      } else {
        setDiscountType('FLAT');
        setDiscountValue(String(d.value));
        setSelectedCoupon(null);
      }
      setHasUnsavedDiscount(false);
    } else if (currentOrder && !currentOrder.discount) {
      setDiscountType(null);
      setDiscountValue('');
      setSelectedCoupon(null);
      setHasUnsavedDiscount(false);
    }
  }, [currentOrder?.discount]);

  const computedDiscount = useMemo(() => {
    if (!currentOrder || !discountType) return 0;
    const total = Number(currentOrder.totalAmount || 0);
    if (discountType === 'FLAT') {
      const v = Number(discountValue) || 0;
      return Math.min(v, total);
    }
    if (discountType === 'PERCENT') {
      const v = Number(discountValue) || 0;
      return Math.min(Math.round(total * v) / 100, total);
    }
    if (discountType === 'COUPON' && selectedCoupon) {
      if (selectedCoupon.type === 'PERCENTAGE') {
        let amt = total * Number(selectedCoupon.value) / 100;
        if (selectedCoupon.maxDiscountValue) amt = Math.min(amt, Number(selectedCoupon.maxDiscountValue));
        return Math.min(Math.round(amt * 100) / 100, total);
      }
      return Math.min(Number(selectedCoupon.value), total);
    }
    return 0;
  }, [currentOrder, discountType, discountValue, selectedCoupon]);

  const handleDiscountTypeChange = useCallback((type) => {
    if (type === null) {
      // Clear discount
      setDiscountType(null);
      setDiscountValue('');
      setSelectedCoupon(null);
      setHasUnsavedDiscount(currentOrder?.discount != null);
    } else {
      setDiscountType(type);
      if (type !== 'COUPON') {
        setSelectedCoupon(null);
        setDiscountValue('');
      }
      setHasUnsavedDiscount(true);
    }
    setDiscountMenuVisible(false);
  }, [currentOrder?.discount]);

  const handleCouponSelect = useCallback((coupon) => {
    setSelectedCoupon(coupon);
    setDiscountValue(String(coupon.value));
    setHasUnsavedDiscount(true);
  }, []);

  const handleSaveDiscount = useCallback(async () => {
    if (!currentOrder) return;
    setSavingDiscount(true);
    try {
      if (discountType === null) {
        await removeDiscount(currentOrder.id);
      } else {
        const data = {discountType};
        if (discountType === 'COUPON') {
          data.couponId = selectedCoupon?.id;
        } else {
          data.value = Number(discountValue) || 0;
        }
        await applyDiscount(currentOrder.id, data);
      }
      setHasUnsavedDiscount(false);
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.message || e.message);
    } finally {
      setSavingDiscount(false);
    }
  }, [currentOrder, discountType, discountValue, selectedCoupon, applyDiscount, removeDiscount, t]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchOrderDetail(orderId);
    setRefreshing(false);
  }, [orderId, fetchOrderDetail]);

  const handleOpenStatusModal = useCallback(() => {
    if (currentOrder) {
      setSelectedStatus(currentOrder.status);
      setStatusModalVisible(true);
    }
  }, [currentOrder]);

  const handleStatusChange = useCallback(async () => {
    if (!currentOrder || !selectedStatus || selectedStatus === currentOrder.status) {
      setStatusModalVisible(false);
      return;
    }
    try {
      await updateOrderStatus(currentOrder.id, selectedStatus);
      setStatusModalVisible(false);
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.message || e.message);
    }
  }, [currentOrder, selectedStatus, updateOrderStatus, t]);

  const handleInstanceStatusChange = useCallback(async () => {
    if (!instanceStatusTarget || !selectedInstanceStatus) {
      setInstanceStatusModalVisible(false);
      return;
    }
    try {
      await updateInstanceStatus(instanceStatusTarget.orderId, instanceStatusTarget.instanceId, selectedInstanceStatus);
      setInstanceStatusModalVisible(false);
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.message || e.message);
    }
  }, [instanceStatusTarget, selectedInstanceStatus, updateInstanceStatus, t]);

  const handleDateChange = useCallback(async (event, date) => {
    setShowDatePicker(false);
    if (event.type === 'dismissed' || !date || !currentOrder) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const formatted = `${y}-${m}-${d}`;
    if (formatted === currentOrder.orderDate) return;
    try {
      await updateOrderDate(currentOrder.id, formatted);
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.message || e.message);
    }
  }, [currentOrder, updateOrderDate, t]);

  const handleDeleteLine = useCallback(async (lineId) => {
    Alert.alert(
      'Delete Item',
      'Are you sure you want to delete this item?',
      [
        {text: 'Cancel', style: 'cancel'},
        {text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            await deleteLine(orderId, lineId);
          } catch (e) {
            Alert.alert(t('common.error'), e.response?.data?.message || e.message);
          }
        }},
      ]
    );
  }, [orderId, deleteLine, t]);

  const handleEditLine = useCallback((line) => {
    navigation.navigate('OrderLineDetail', {
      catalogItemId: line.catalogItemId,
      catalogItemName: line.catalogItemName,
      customerId: currentOrder.customerId,
      customerName: currentOrder.customerName,
      orderDate: currentOrder.orderDate,
      deliveryDate: currentOrder.deliveryDate,
      urgent: currentOrder.urgent,
      orderId: currentOrder.id,
      editLine: line,
    });
  }, [currentOrder, navigation]);

  const handleAddItem = useCallback(() => {
    if (!currentOrder) return;
    navigation.navigate('SelectItem', {
      customerId: currentOrder.customerId,
      customerName: currentOrder.customerName,
      customerGender: null,
      orderDate: currentOrder.orderDate,
      deliveryDate: currentOrder.deliveryDate,
      urgent: currentOrder.urgent,
      notes: currentOrder.notes,
      orderId: currentOrder.id,
      existingLines: currentOrder.lines || [],
    });
  }, [currentOrder, navigation]);

  const handlePrintLine = useCallback(async (line) => {
    const orderNumber = currentOrder?.orderNumber || '';
    const customerName = currentOrder?.customerName || '';
    const catalogItemName = line.catalogItemName || '';
    const deliveryDate = line.deliveryDate || currentOrder?.deliveryDate || '';
    const specialInstructions = line.specialInstructions || '';

    // Prefer instance-level measurements (from profile), fall back to line-level
    const inst = line.instances && line.instances.length > 0 ? line.instances[0] : null;
    const measurements = (inst?.measurements && inst.measurements.length > 0)
      ? inst.measurements
      : (line.measurements || []);
    const measurementRows = measurements.length > 0
      ? measurements.map(m =>
          `<tr><td style="border:1px solid #ccc;padding:6px;text-align:center;font-weight:bold;">${m.globalOptionName || m.customOptionName || m.measurementTypeName || 'Unknown'}</td><td style="border:1px solid #ccc;padding:6px;text-align:center;">${m.value ?? ''}</td></tr>`
        ).join('')
      : '<tr><td colspan="2" style="padding:8px;text-align:center;">--NONE--</td></tr>';
    let optionsHtml = '<div style="padding:8px;text-align:center;">--NONE--</div>';
    const optionSelectionsJson = inst?.optionSelections || inst?.stitchFeatureSelections;
    if (optionSelectionsJson) {
      try {
        const features = JSON.parse(optionSelectionsJson);
        if (Array.isArray(features) && features.length > 0) {
          const rows = features.map(f => {
            const name = f.optionTypeName || f.stitchFeatureName || '';
            let value = '--';
            if (f.renderType === 'TEXT' && f.textValue) {
              value = f.textValue;
            } else if (f.selectedOptions && f.selectedOptions.length > 0) {
              value = f.selectedOptions.map(o => o.name).join(', ');
            }
            return `<tr><td style="border:1px solid #ccc;padding:6px;text-align:center;font-weight:bold;">${name}</td><td style="border:1px solid #ccc;padding:6px;text-align:center;">${value}</td></tr>`;
          }).join('');
          optionsHtml = `<table style="width:80%;margin:10px auto;border-collapse:collapse;">${rows}</table>`;
        }
      } catch (e) {
        optionsHtml = `<div style="padding:8px;">${optionSelectionsJson}</div>`;
      }
    }

    const html = `
      <div style="max-width:700px;margin:auto;font-family:Arial,sans-serif;">
        <div style="background:#444;color:#fff;text-align:center;padding:10px;font-weight:bold;">
          ORDER # ${orderNumber}
        </div>
        <table width="100%" style="border-collapse:collapse;">
          <tr style="background:#f5f5f5;"><td style="padding:8px;font-weight:bold;">Customer Name</td><td style="padding:8px;">${customerName}</td></tr>
          <tr><td style="padding:8px;font-weight:bold;">Dress Type</td><td style="padding:8px;">${catalogItemName}</td></tr>
          <tr style="background:#f5f5f5;"><td style="padding:8px;font-weight:bold;">Delivery Date</td><td style="padding:8px;">${deliveryDate}</td></tr>
        </table>
        <div style="background:#888;color:#fff;padding:8px;font-weight:bold;">Special Instructions</div>
        <div style="padding:8px;">${specialInstructions || '--NONE--'}</div>
        <div style="background:#888;color:#fff;padding:8px;font-weight:bold;">Measurements</div>
        <table style="width:80%;margin:10px auto;border-collapse:collapse;">${measurementRows}</table>
        <div style="background:#888;color:#fff;padding:8px;font-weight:bold;">Options</div>
        ${optionsHtml}
      </div>
    `;

    try {
      await RNPrint.print({html});
    } catch (e) {
      if (!e.message?.includes('cancelled')) {
        Alert.alert(t('common.error'), e.message);
      }
    }
  }, [currentOrder, t]);

  if (isLoading && !currentOrder) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const order = currentOrder;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={order?.orderNumber || 'Order'} titleStyle={styles.appbarTitle} />
        <Appbar.Action icon="plus" onPress={handleAddItem} color={colors.white} />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {order && (
          <>
            {/* Order Header */}
            <Card style={styles.headerCard}>
              <Card.Content>
                <View style={styles.row}>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => navigation.navigate('Customers', {screen: 'CustomerDetail', params: {customerId: order.customerId}})}>
                    <Text variant="titleMedium" style={styles.customerNameLink}>{order.customerName}</Text>
                  </TouchableOpacity>
                  {order.urgent && <Chip style={styles.urgentChip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>Urgent</Text></Chip>}
                </View>
                {order.customerPhone && (
                  <View style={styles.phoneRow}>
                    <Text variant="bodySmall" style={styles.label}>{order.customerPhone}</Text>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => Linking.openURL(`tel:${order.customerPhone}`)}
                      style={styles.phoneAction}>
                      <Icon name="phone" size={18} color={colors.success} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => {
                        const num = order.customerPhone.replace(/[^0-9]/g, '');
                        const whatsappNum = num.length === 10 ? `91${num}` : num;
                        Linking.openURL(`https://wa.me/${whatsappNum}`);
                      }}
                      style={styles.phoneAction}>
                      <Icon name="whatsapp" size={18} color="#25D366" />
                    </TouchableOpacity>
                  </View>
                )}
                <Text variant="bodySmall" style={styles.label}>
                  {order.orderNumber}
                </Text>
                <View style={styles.statusDateRow}>
                  <View style={styles.statusDateItem}>
                    <Chip
                      style={[styles.orderStatusChip, {backgroundColor: (colors.status[order.status] || colors.info) + '20'}]}>
                      <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t(`order.status.${order.status}`)}</Text>
                    </Chip>
                    <IconButton icon="pencil" size={16} iconColor={colors.textSecondary}
                      style={styles.editIconBtn} onPress={handleOpenStatusModal} />
                  </View>
                  <View style={styles.statusDateItem}>
                    <Text variant="bodySmall" style={styles.dateLabel}>
                      {formatDateDdMmm(order.orderDate) || 'No date'}
                    </Text>
                    <IconButton icon="pencil" size={16} iconColor={colors.textSecondary}
                      style={styles.editIconBtn} onPress={() => setShowDatePicker(true)} />
                  </View>
                </View>
              </Card.Content>
            </Card>

            {/* Order Line Cards */}
            {order.lines && order.lines.length > 0 && (
              <View style={styles.section}>
                <Text variant="titleMedium" style={styles.sectionTitle}>
                  Items ({order.lines.length})
                </Text>
                {order.lines.map((line) => {
                  const inst = line.instances && line.instances.length > 0 ? line.instances[0] : null;
                  const lineStatus = inst ? inst.status : line.status;
                  return (
                    <Card key={line.id} style={styles.instanceCard}>
                      <Card.Content>
                        <TouchableOpacity activeOpacity={0.7} onPress={() => {
                          if (inst) {
                            navigation.navigate('OrderLineInstanceView', {
                              instance: inst,
                              line,
                              orderNumber: order.orderNumber,
                              orderDate: order.orderDate,
                            });
                          }
                        }}>
                        <View style={styles.cardTop}>
                          <TouchableOpacity
                            activeOpacity={0.7}
                            style={styles.imageColumn}
                            onPress={() => setImageModalLine(line)}>
                            {line.clothImageUrls?.[0] ? (
                              <Image source={{uri: line.clothImageUrls[0]}} style={styles.clothThumb} />
                            ) : (
                              <View style={styles.clothThumbPlaceholder}>
                                <Icon name="image-outline" size={24} color={colors.textSecondary} />
                              </View>
                            )}
                            {line.patternImageUrls?.[0] && (
                              <Image source={{uri: line.patternImageUrls[0]}} style={styles.patternThumb} />
                            )}
                          </TouchableOpacity>
                          <View style={{flex: 1}}>
                            <View style={styles.instanceHeader}>
                              <View style={{flex: 1}}>
                                <Text variant="titleSmall" style={styles.catalogItemName}>{line.catalogItemName}</Text>
                                {line.itemName && (
                                  <Text variant="bodySmall" style={[styles.label, {fontSize: 14}]}>{line.itemName}</Text>
                                )}
                                {line.deliveryDate && (
                                  <Text variant="bodySmall" style={styles.deliveryDateText}>{formatDateDdMmm(line.deliveryDate)}</Text>
                                )}
                              </View>
                              <View style={{alignItems: 'flex-end'}}>
                                <View style={styles.statusUrgentRow}>
                                  {line.urgent && (
                                    <Icon name="flag" size={18} color={colors.error} style={{marginRight: 4}} />
                                  )}
                                  <Chip
                                    style={[styles.statusChip, {backgroundColor: (colors.status[lineStatus] || colors.info) + '20'}]}>
                                    <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{t(`order.status.${lineStatus}`)}</Text>
                                  </Chip>
                                  {inst && (
                                    <IconButton icon="pencil-outline" size={14} iconColor={colors.textSecondary}
                                      style={{margin: 0, marginLeft: -4}}
                                      onPress={() => {
                                        setInstanceStatusTarget({orderId: order.id, instanceId: inst.id});
                                        setSelectedInstanceStatus(lineStatus);
                                        setInstanceStatusModalVisible(true);
                                      }} />
                                  )}
                                </View>
                                <Text variant="bodySmall" style={styles.unitPriceText}>
                                  {'\u20B9'}{Number(line.unitPrice || 0).toLocaleString()}
                                </Text>
                              </View>
                            </View>
                            {line.addOnLines && line.addOnLines.length > 0 && (
                              <View style={styles.addOnsSection}>
                                {line.addOnLines.map(ao => (
                                  <View key={ao.id} style={styles.addOnRow}>
                                    <Text variant="bodySmall" style={styles.addOnName}>+ {ao.itemName}</Text>
                                    <Text variant="bodySmall" style={styles.addOnPrice}>{'\u20B9'}{ao.unitPrice}</Text>
                                  </View>
                                ))}
                                <View style={styles.subTotalRow}>
                                  <View style={styles.subTotalLine} />
                                  <Text variant="bodySmall" style={styles.subTotalText}>
                                    {'\u20B9'}{(Number(line.unitPrice || 0) + line.addOnLines.reduce((sum, ao) => sum + Number(ao.unitPrice || 0), 0)).toLocaleString()}
                                  </Text>
                                </View>
                              </View>
                            )}
                          </View>
                        </View>
                        </TouchableOpacity>

                        <View style={styles.instanceActions}>
                          <IconButton icon="pencil" size={18} iconColor={colors.primary}
                            onPress={() => handleEditLine(line)} />
                          {(() => {
                            const instAssignments = inst ? (instanceAssignments[inst.id] || []) : [];
                            const active = instAssignments.filter(a => a.status !== 'DELETED' && a.status !== 'COMPLETED');
                            const completed = instAssignments.filter(a => a.status === 'COMPLETED');
                            const total = active.length + completed.length;
                            let workerIcon = 'account-outline';
                            let workerColor = colors.textSecondary;
                            if (total === 1) {
                              workerIcon = 'account';
                              workerColor = active.length > 0 ? colors.primary : colors.success;
                            } else if (total > 1) {
                              workerIcon = 'account-group';
                              workerColor = active.length > 0 ? colors.primary : colors.success;
                            }
                            return (
                              <IconButton icon={workerIcon} size={18} iconColor={workerColor}
                                onPress={() => {
                                  if (inst) {
                                    setAssignModalTarget({
                                      instanceId: inst.id,
                                      catalogItemId: line.catalogItemId,
                                      serviceCatalogItemId: line.serviceCatalogItemId,
                                    });
                                  }
                                }} />
                            );
                          })()}
                          <IconButton icon="clipboard-text-outline" size={18} iconColor={colors.info}
                            onPress={() => {
                              if (inst) {
                                navigation.navigate('InstanceTask', {
                                  orderId: order.id,
                                  instanceId: inst.id,
                                  deliveryDate: line.deliveryDate || order.deliveryDate,
                                  instanceCode: inst.instanceCode,
                                });
                              }
                            }} />
                          <IconButton icon="printer" size={18} iconColor={colors.text}
                            onPress={() => handlePrintLine(line)} />
                          <IconButton icon="delete" size={18} iconColor={colors.error}
                            onPress={() => handleDeleteLine(line.id)} />
                        </View>
                      </Card.Content>
                    </Card>
                  );
                })}
              </View>
            )}

            {/* Financial Summary */}
            <Card style={styles.financialCard}>
              <Card.Content>
                <View style={styles.row}>
                  <Text variant="titleMedium" style={styles.sectionTitle}>Financial Summary</Text>
                  {order.paymentStatus && order.paymentStatus !== 'UNPAID' && (
                    <Chip
                      style={[styles.paymentStatusChip, {
                        backgroundColor: (order.paymentStatus === 'PAID' ? colors.success : colors.warning) + '20',
                      }]}>
                      <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>
                        {order.paymentStatus === 'PAID' ? 'Paid' : 'Partial'}
                      </Text>
                    </Chip>
                  )}
                </View>
                <PriceRow label="Total Bill Amount" value={order.totalAmount} />

                {/* Discount Row */}
                <View style={styles.discountRow}>
                  <Menu
                    visible={discountMenuVisible}
                    onDismiss={() => setDiscountMenuVisible(false)}
                    anchor={
                      <Button
                        mode="outlined"
                        compact
                        onPress={() => setDiscountMenuVisible(true)}
                        style={styles.discountMenuBtn}
                        labelStyle={styles.discountMenuLabel}
                        icon="chevron-down"
                        contentStyle={{flexDirection: 'row-reverse'}}>
                        {discountType === 'FLAT' ? 'Disc/AMT' : discountType === 'PERCENT' ? 'Disc/%' : discountType === 'COUPON' ? 'PROMO' : 'Discount'}
                      </Button>
                    }>
                    <Menu.Item title="Disc / AMT" onPress={() => handleDiscountTypeChange('FLAT')} />
                    <Menu.Item title="Disc / PER(%)" onPress={() => handleDiscountTypeChange('PERCENT')} />
                    <Menu.Item title="PROMO (Coupon)" onPress={() => handleDiscountTypeChange('COUPON')} />
                    {discountType && <Menu.Item title="Clear Discount" onPress={() => handleDiscountTypeChange(null)} />}
                  </Menu>

                  {discountType === 'COUPON' ? (
                    <TouchableOpacity
                      style={styles.couponChooseBtn}
                      onPress={() => setCouponModalVisible(true)}>
                      <Text style={styles.couponChooseText}>
                        {selectedCoupon ? selectedCoupon.code : '(Choose)'}
                      </Text>
                    </TouchableOpacity>
                  ) : discountType ? (
                    <TextInput
                      mode="outlined"
                      placeholder={discountType === 'PERCENT' ? '%' : 'Amount'}
                      value={discountValue}
                      onChangeText={v => { setDiscountValue(v); setHasUnsavedDiscount(true); }}
                      keyboardType="numeric"
                      style={styles.discountInput}
                      dense
                    />
                  ) : (
                    <View style={{flex: 1}} />
                  )}

                  <Text style={styles.discountAmount}>
                    {computedDiscount > 0 ? `-\u20B9${computedDiscount.toLocaleString()}` : '-'}
                  </Text>
                </View>

                {hasUnsavedDiscount && (
                  <Button
                    mode="contained"
                    compact
                    onPress={handleSaveDiscount}
                    loading={savingDiscount}
                    disabled={savingDiscount || (discountType && discountType !== 'COUPON' && !Number(discountValue)) || (discountType === 'COUPON' && !selectedCoupon)}
                    style={styles.updateDiscountBtn}
                    labelStyle={{fontSize: 12}}>
                    UPDATE
                  </Button>
                )}

                {Number(order.taxAmount) > 0 && (
                  <PriceRow label="Tax" value={order.taxAmount} />
                )}

                <PriceRow label="Payments Received" value={order.advanceAmount} />
                <Divider style={styles.divider} />
                <PriceRow label="Balance Due" value={order.balanceAmount} bold color={colors.error} />
              </Card.Content>
            </Card>

            {/* Action Buttons */}
            <View style={styles.actionGrid}>
              <Button mode="outlined" style={styles.actionBtn}
                onPress={() => setPaymentModalVisible(true)}>
                Receive Payment
              </Button>
              <Button mode="outlined" style={styles.actionBtn}
                onPress={() => navigation.navigate('OrderBill', {order})}>
                View Bill
              </Button>
            </View>

            <View style={{height: spacing.xxl}} />
          </>
        )}
      </ScrollView>

      <ReceivePaymentModal
        visible={paymentModalVisible}
        onDismiss={() => setPaymentModalVisible(false)}
        order={order}
        onPaymentComplete={handleRefresh}
      />

      <CouponSelectModal
        visible={couponModalVisible}
        onDismiss={() => setCouponModalVisible(false)}
        onSelect={handleCouponSelect}
        orderTotal={order?.totalAmount}
      />

      <OrderLineImagesModal
        visible={!!imageModalLine}
        onDismiss={(shouldRefresh) => {
          setImageModalLine(null);
          if (shouldRefresh) {
            fetchOrderDetail(orderId);
          }
        }}
        orderId={orderId}
        line={imageModalLine}
      />

      <Portal>
        <Dialog visible={statusModalVisible} onDismiss={() => setStatusModalVisible(false)}
          style={styles.statusDialog}>
          <Dialog.Title>{t('order.updateStatus')}</Dialog.Title>
          <Dialog.Content>
            <RadioButton.Group value={selectedStatus} onValueChange={setSelectedStatus}>
              {ALL_STATUSES.map(s => (
                <RadioButton.Item
                  key={s}
                  label={t(`order.status.${s}`)}
                  value={s}
                  labelStyle={{color: colors.status[s] || colors.text}}
                />
              ))}
            </RadioButton.Group>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setStatusModalVisible(false)}>{t('common.cancel')}</Button>
            <Button onPress={handleStatusChange}>{t('common.save')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Portal>
        <Dialog visible={instanceStatusModalVisible} onDismiss={() => setInstanceStatusModalVisible(false)}
          style={styles.statusDialog}>
          <Dialog.Title>Update Instance Status</Dialog.Title>
          <Dialog.Content>
            <RadioButton.Group value={selectedInstanceStatus} onValueChange={setSelectedInstanceStatus}>
              {ALL_STATUSES.map(s => (
                <RadioButton.Item
                  key={s}
                  label={t(`order.status.${s}`)}
                  value={s}
                  labelStyle={{color: colors.status[s] || colors.text}}
                />
              ))}
            </RadioButton.Group>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setInstanceStatusModalVisible(false)}>{t('common.cancel')}</Button>
            <Button onPress={handleInstanceStatusChange}>{t('common.save')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {showDatePicker && (
        <DateTimePicker
          value={order?.orderDate ? new Date(order.orderDate + 'T00:00:00') : new Date()}
          mode="date"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={handleDateChange}
        />
      )}

      {assignModalTarget && (
        <WorkAssignmentModal
          visible={!!assignModalTarget}
          onDismiss={() => {
            setAssignModalTarget(null);
            fetchAllAssignments();
          }}
          orderLineInstanceId={assignModalTarget.instanceId}
          catalogItemId={assignModalTarget.catalogItemId}
          serviceCatalogItemId={assignModalTarget.serviceCatalogItemId}
        />
      )}
    </View>
  );
};

const PriceRow = ({label, value, bold, color}) => (
  <View style={styles.priceRow}>
    <Text variant={bold ? 'titleSmall' : 'bodyMedium'}
      style={[bold && styles.boldText, color && {color}]}>{label}</Text>
    <Text variant={bold ? 'titleSmall' : 'bodyMedium'}
      style={[bold && styles.boldText, color && {color}]}>
      {'\u20B9'}{Number(value || 0).toLocaleString()}
    </Text>
  </View>
);

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  headerCard: {margin: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  row: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  customerName: {fontWeight: 'bold', color: colors.text, flex: 1},
  customerNameLink: {fontWeight: 'bold', color: colors.primary, flex: 1, textDecorationLine: 'underline'},
  urgentChip: {backgroundColor: colors.error + '20', height: 24, paddingVertical: 0},
  urgentText: {color: colors.error, fontSize: 12, lineHeight: 14},
  phoneRow: {flexDirection: 'row', alignItems: 'center', marginTop: 2},
  phoneAction: {marginLeft: spacing.sm, padding: 2},
  label: {color: colors.textSecondary},
  section: {marginHorizontal: spacing.md, marginBottom: spacing.md},
  sectionTitle: {fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  instanceCard: {marginBottom: spacing.sm, backgroundColor: colors.surface, borderRadius: 8},
  cardTop: {flexDirection: 'row', alignItems: 'flex-start'},
  imageColumn: {width: '20%', marginRight: spacing.sm},
  clothThumb: {width: '100%', aspectRatio: 1, borderRadius: 8},
  clothThumbPlaceholder: {width: '100%', aspectRatio: 1, borderRadius: 8, backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center'},
  patternThumb: {width: '100%', aspectRatio: 1, borderRadius: 8, marginTop: spacing.xs},
  instanceHeader: {flexDirection: 'row', alignItems: 'flex-start'},
  instanceCode: {color: colors.primary, fontWeight: '500', fontSize: 11},
  catalogItemName: {color: colors.text, fontWeight: '600', fontSize: 16},
  deliveryDateText: {color: colors.textSecondary, fontSize: 14, marginTop: 2},
  statusUrgentRow: {flexDirection: 'row', alignItems: 'center'},
  statusChip: {height: 24, marginLeft: spacing.sm, paddingVertical: 0},
  unitPriceText: {color: colors.text, fontWeight: '600', fontSize: 15, marginTop: 4},
  addOnsSection: {marginTop: spacing.xs},
  addOnRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  addOnName: {color: colors.textSecondary, fontSize: 14, flex: 1},
  addOnPrice: {color: colors.textSecondary, fontSize: 14, textAlign: 'right'},
  subTotalRow: {alignItems: 'flex-end', marginTop: 4},
  subTotalLine: {width: 80, height: 1, backgroundColor: colors.textSecondary, marginBottom: 4},
  subTotalText: {color: colors.text, fontWeight: '700', fontSize: 15},
  instanceActions: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: spacing.xs},
  financialCard: {marginHorizontal: spacing.md, marginBottom: spacing.md, backgroundColor: colors.surface, borderRadius: 12},
  priceRow: {flexDirection: 'row', justifyContent: 'space-between', marginBottom: spacing.xs},
  boldText: {fontWeight: 'bold', color: colors.text},
  divider: {marginVertical: spacing.sm},
  actionGrid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm,
    marginHorizontal: spacing.md, marginBottom: spacing.md,
  },
  actionBtn: {width: '47%', borderColor: colors.border},
  paymentStatusChip: {height: 24, marginLeft: spacing.sm, paddingVertical: 0},
  discountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    gap: 4,
  },
  discountMenuBtn: {borderColor: colors.border, minWidth: 90},
  discountMenuLabel: {fontSize: 11},
  discountInput: {flex: 1, fontSize: 13, height: 36},
  discountAmount: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.success,
    minWidth: 60,
    textAlign: 'right',
  },
  couponChooseBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  couponChooseText: {fontSize: 13, color: colors.primary},
  statusDateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  statusDateItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  orderStatusChip: {height: 26, paddingVertical: 0},
  editIconBtn: {margin: 0, marginLeft: 2},
  dateLabel: {color: colors.textSecondary, fontSize: 13},
  statusDialog: {borderRadius: 12},
  updateDiscountBtn: {
    alignSelf: 'flex-end',
    marginBottom: spacing.sm,
    backgroundColor: colors.primary,
  },
});

export default OrderViewScreen;
